import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Utensils, Clock, ChevronDown, ShoppingCart, RefreshCw,
  Loader2, Download, Apple, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { fetchApi } from '../lib/utils';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLanguage } from '../context/LanguageContext';

export default function Nutrition() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await fetchApi('/nutrition');
      setPlans(data);
      if (data.length > 0) {
        setActivePlan(data[0]);
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        const todayPlan = data[0].daily_plans?.find(d => d.day === today);
        setSelectedDay(todayPlan?.day || data[0].daily_plans?.[0]?.day);
      }
    } catch (error) {
      console.error('Load plans error:', error);
    } finally {
      setLoading(false);
    }
  };

  const regeneratePlan = async () => {
    setRegenerating(true);
    try {
      const newPlan = await fetchApi('/nutrition/generate', { method: 'POST' });
      setPlans([newPlan, ...plans]);
      setActivePlan(newPlan);
      setSelectedDay(newPlan.daily_plans?.[0]?.day);
    } catch (error) {
      console.error('Regenerate error:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const generateShoppingListPDF = () => {
    if (!activePlan?.daily_plans) {
      console.error('No active plan or daily plans available');
      return;
    }
    
    try {
      // Use pre-calculated shopping list if available, otherwise calculate from meals
      let shoppingItems = [];
      
      if (activePlan.shopping_list?.length > 0) {
        // Use the AI-generated shopping list with aggregated quantities
        shoppingItems = activePlan.shopping_list.map(item => ({
          name: item.name,
          quantity: `${item.total_quantity} ${item.unit}`,
          category: item.category || 'Other'
        }));
      } else {
        // Fallback: Calculate from meal ingredients
        const ingredientMap = new Map();
        activePlan.daily_plans.forEach(day => {
          day.meals?.forEach(meal => {
            meal.ingredients?.forEach(ing => {
              if (typeof ing === 'object' && ing.name) {
                const key = ing.name;
                if (!ingredientMap.has(key)) {
                  ingredientMap.set(key, { name: ing.name, quantities: [], unit: ing.unit || '' });
                }
                if (ing.quantity) {
                  ingredientMap.get(key).quantities.push(parseFloat(ing.quantity) || 0);
                }
              } else {
                ingredientMap.set(String(ing), { name: String(ing), quantities: [1], unit: '' });
              }
            });
          });
        });
        
        // Aggregate quantities
        shoppingItems = Array.from(ingredientMap.values()).map(item => ({
          name: item.name,
          quantity: item.quantities.length > 0 
            ? `${item.quantities.reduce((a, b) => a + b, 0).toFixed(0)} ${item.unit}`.trim()
            : 'As needed',
          category: 'General'
        }));
      }
      
      // Sort by category then name
      shoppingItems.sort((a, b) => {
        if (a.category !== b.category) return a.category.localeCompare(b.category);
        return a.name.localeCompare(b.name);
      });
      
      // Handle empty ingredient list
      if (shoppingItems.length === 0) {
        alert('No ingredients found in your meal plan. Please generate a nutrition plan first.');
        return;
      }
      
      // Create PDF
      const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // VYTA Brand Colors
    const brandYellow = [204, 255, 0];
    const brandBlack = [0, 0, 0];
    const brandGray = [40, 40, 40];
    const textGray = [120, 120, 120];
    
    // Header background
    doc.setFillColor(...brandBlack);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Logo/Brand
    doc.setFillColor(...brandYellow);
    doc.roundedRect(15, 12, 22, 22, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...brandBlack);
    doc.text('V', 22, 27);
    
    // Brand name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('VYTA', 42, 26);
    
    // Tagline
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...brandYellow);
    doc.text('Training. Nutrition. Performance.', 42, 33);
    
    // Title
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brandBlack);
    doc.text('Shopping List', 15, 65);
    
    // Subtitle with plan name
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textGray);
    doc.text(`For: ${activePlan.name || 'Your Personalized Nutrition Plan'}`, 15, 75);
    
    // Date generated
    const today = new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated: ${today}`, 15, 82);
    
    // Decorative line
    doc.setDrawColor(...brandYellow);
    doc.setLineWidth(2);
    doc.line(15, 88, pageWidth - 15, 88);
    
    // Macros summary box
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(15, 94, pageWidth - 30, 25, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brandBlack);
    doc.text('DAILY TARGETS', 20, 103);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const macroY = 113;
    const macroSpacing = (pageWidth - 50) / 4;
    
    doc.setTextColor(...brandBlack);
    doc.text(`${activePlan.calorie_target || '—'} kcal`, 25, macroY);
    doc.setTextColor(220, 80, 80);
    doc.text(`${activePlan.protein_target || '—'}g protein`, 25 + macroSpacing, macroY);
    doc.setTextColor(100, 160, 80);
    doc.text(`${activePlan.carbs_target || '—'}g carbs`, 25 + macroSpacing * 2, macroY);
    doc.setTextColor(100, 100, 220);
    doc.text(`${activePlan.fat_target || '—'}g fat`, 25 + macroSpacing * 3, macroY);
    
    // Shopping list table - group by category
    const groupedItems = shoppingItems.reduce((acc, item) => {
      const cat = item.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    
    const tableData = [];
    Object.entries(groupedItems).forEach(([category, items]) => {
      // Category header row
      tableData.push([{ content: category.toUpperCase(), colSpan: 3, styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: [20, 184, 166] } }]);
      // Items in category
      items.forEach(item => {
        tableData.push(['☐', item.name, item.quantity]);
      });
    });
    
    autoTable(doc, {
      startY: 128,
      head: [['', 'Ingredient', 'Quantity']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: brandBlack,
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 10,
        textColor: [60, 60, 60],
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 35, halign: 'right', textColor: [204, 255, 0], fontStyle: 'bold' },
      },
      alternateRowStyles: {
        fillColor: [252, 252, 252],
      },
      margin: { left: 15, right: 15 },
      didDrawPage: function(data) {
        // Footer on each page
        const footerY = pageHeight - 15;
        doc.setFillColor(...brandBlack);
        doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(...brandYellow);
        doc.text('VYTA', 15, footerY);
        
        doc.setTextColor(150, 150, 150);
        doc.text('Your personalized fitness & nutrition partner', 35, footerY);
        
        doc.text(`Page ${data.pageNumber}`, pageWidth - 25, footerY);
      }
    });
    
    // Tips section (if fits on page)
    const finalY = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || 180) + 15;
    if (finalY < pageHeight - 60) {
      doc.setFillColor(255, 252, 230);
      doc.roundedRect(15, finalY, pageWidth - 30, 35, 3, 3, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...brandBlack);
      doc.text('💡 Shopping Tips', 20, finalY + 10);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...textGray);
      doc.text('• Buy fresh produce for the first few days, frozen for later in the week', 20, finalY + 20);
      doc.text('• Check your pantry for staples before shopping to avoid duplicates', 20, finalY + 28);
    }
    
    // Save PDF
    doc.save('vyta-shopping-list.pdf');
    console.log('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const currentDayPlan = activePlan?.daily_plans?.find(d => d.day === selectedDay);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-lg font-bold">Nutrition Plan</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={regeneratePlan}
            disabled={regenerating}
            className="text-zinc-400 hover:text-white"
            data-testid="regenerate-btn"
          >
            {regenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {activePlan ? (
          <>
            {/* Plan Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-default mb-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black mb-2">{activePlan.name}</h2>
                  <p className="text-zinc-400">{activePlan.description}</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] text-xs font-bold">
                  Personalized
                </span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 rounded-xl bg-zinc-900">
                  <p className="text-2xl font-black text-[#CCFF00]">{activePlan.calorie_target}</p>
                  <p className="text-xs text-zinc-400">Calories</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-zinc-900">
                  <p className="text-2xl font-black text-red-400">{activePlan.protein_target}g</p>
                  <p className="text-xs text-zinc-400">Protein</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-zinc-900">
                  <p className="text-2xl font-black text-[#CCFF00]">{activePlan.carbs_target}g</p>
                  <p className="text-xs text-zinc-400">Carbs</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-zinc-900">
                  <p className="text-2xl font-black text-[#6366F1]">{activePlan.fat_target}g</p>
                  <p className="text-xs text-zinc-400">Fat</p>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={generateShoppingListPDF}
                className="w-full border-zinc-700 hover:bg-zinc-900"
                data-testid="shopping-list-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Download Shopping List (PDF)
              </Button>
            </motion.div>

            {/* Day Selector */}
            <div className="mb-6 overflow-x-auto hide-scrollbar">
              <div className="flex gap-2 pb-2">
                {activePlan.daily_plans?.map((day) => {
                  const isToday = day.day === new Date().toLocaleDateString('en-US', { weekday: 'long' });
                  const isSelected = selectedDay === day.day;
                  
                  return (
                    <button
                      key={day.day}
                      onClick={() => setSelectedDay(day.day)}
                      className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                        isSelected
                          ? 'bg-[#CCFF00] text-black font-bold'
                          : isToday
                          ? 'bg-[#CCFF00]/20 text-[#CCFF00]'
                          : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                      }`}
                      data-testid={`day-${day.day.toLowerCase()}`}
                    >
                      {day.day.slice(0, 3)}
                      {isToday && !isSelected && <span className="ml-1">•</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Daily Plan */}
            {currentDayPlan && (
              <motion.div
                key={selectedDay}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{selectedDay}&apos;s Meals</h3>
                  <div className="text-sm text-zinc-400">
                    {currentDayPlan.total_calories} kcal
                  </div>
                </div>

                <div className="space-y-4">
                  {currentDayPlan.meals?.map((meal, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="card-default"
                      data-testid={`meal-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                          <Utensils className="w-5 h-5 text-[#14B8A6]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-bold">{meal.name}</h4>
                              <p className="text-sm text-zinc-400">{meal.description}</p>
                            </div>
                            <div className="text-right text-xs text-zinc-500">
                              {meal.prep_time && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> Prep: {meal.prep_time}</div>}
                              {meal.cook_time && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> Cook: {meal.cook_time}</div>}
                            </div>
                          </div>
                          
                          {/* Macros */}
                          <div className="flex flex-wrap gap-3 text-sm mb-4 p-3 rounded-lg bg-zinc-900/50">
                            <span className="font-medium">{meal.calories} kcal</span>
                            <span className="text-red-400">P: {meal.protein}g</span>
                            <span className="text-[#CCFF00]">C: {meal.carbs}g</span>
                            <span className="text-[#6366F1]">F: {meal.fat}g</span>
                            {meal.fiber > 0 && <span className="text-green-400">Fiber: {meal.fiber}g</span>}
                          </div>

                          {/* Ingredients with quantities */}
                          {meal.ingredients?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-zinc-300 mb-2">Ingredients</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {meal.ingredients.map((ing, i) => (
                                  <div
                                    key={i}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 text-sm"
                                  >
                                    <span className="w-2 h-2 rounded-full bg-[#14B8A6]" />
                                    {typeof ing === 'object' ? (
                                      <span>
                                        <span className="text-[#CCFF00] font-medium">{ing.quantity}{ing.unit}</span>
                                        {' '}{ing.name}
                                        {ing.notes && <span className="text-zinc-500"> ({ing.notes})</span>}
                                      </span>
                                    ) : (
                                      <span className="text-zinc-400">{ing}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Prep Instructions */}
                          {meal.prep_instructions?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-zinc-300 mb-2">Prep Instructions</h5>
                              <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400">
                                {meal.prep_instructions.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {/* Cooking Instructions */}
                          {meal.cooking_instructions?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-zinc-300 mb-2">Cooking Instructions</h5>
                              <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400">
                                {meal.cooking_instructions.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {/* Tips */}
                          {meal.tips && (
                            <div className="p-3 rounded-lg bg-[#CCFF00]/5 border border-[#CCFF00]/20">
                              <p className="text-xs text-[#CCFF00]">💡 {meal.tips}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Daily Totals */}
                <div className="mt-6 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <h4 className="font-bold mb-3">Daily Totals</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-zinc-400">Calories</p>
                      <p className="font-bold">{currentDayPlan.total_calories}</p>
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Protein</p>
                      <p className="font-bold text-red-400">{currentDayPlan.total_protein}g</p>
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Carbs</p>
                      <p className="font-bold text-[#CCFF00]">{currentDayPlan.total_carbs}g</p>
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Fat</p>
                      <p className="font-bold text-[#6366F1]">{currentDayPlan.total_fat}g</p>
                    </div>
                  </div>
                </div>

                {/* Weekly Shopping List */}
                {activePlan?.shopping_list?.length > 0 && (
                  <div className="mt-6 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold">Weekly Shopping List</h4>
                      <span className="text-xs text-zinc-500">{activePlan.shopping_list.length} items</span>
                    </div>
                    <div className="space-y-3">
                      {/* Group by category */}
                      {Object.entries(
                        activePlan.shopping_list.reduce((acc, item) => {
                          const cat = item.category || 'Other';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(item);
                          return acc;
                        }, {})
                      ).map(([category, items]) => (
                        <div key={category}>
                          <h5 className="text-sm font-medium text-[#14B8A6] mb-2">{category}</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {items.map((item, i) => (
                              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-800/50 text-sm">
                                <span className="text-zinc-300">{item.name}</span>
                                <span className="text-[#CCFF00] font-medium">{item.total_quantity} {item.unit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Utensils className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Nutrition Plan</h2>
            <p className="text-zinc-400 mb-6">Create your personalized meal plan</p>
            <Button
              onClick={regeneratePlan}
              disabled={regenerating}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
              data-testid="generate-nutrition-btn"
            >
              {regenerating ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              Create My Plan
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
